import '../css/PanelBank.less';

import React, {useEffect, useMemo, useState} from 'react';
import {Loader} from 'semantic-ui-react';

import * as InteractStateActions from '../state/views/interactState/actions';
import * as InteractStateContext from '../state/views/interactState/context';
import Measure from 'react-measure';
import WarningMessage from './elements/WarningMessage';
import PanelBankSections from './PanelBankSections';
import PanelBankControls from './PanelBankControls';
import * as PanelSettingsTypes from '../state/views/panelSettings/types';
import * as PanelBankConfigTypes from '../state/views/panelBankConfig/types';
import * as CustomRunColorsViewTypes from '../state/views/customRunColors/types';
import * as RunSetViewTypes from '../state/views/runSet/types';
import {useKeyInfoQuery} from '../state/runs/hooks';
import {PANEL_BANK_PADDING} from '../util/panelbank';
import {useDebounceState} from '../util/hooks';
import {RunHistoryKeyInfo} from '../types/run';
import {shallowEqual} from 'react-redux';
import {PanelBankContextProvider} from '../state/panelbank/context';
import makeComp from '../util/profiler';
import docUrl from '../util/doc_urls';
import {isReservedKey} from '../util/runs';

export interface PanelBankProps {
  entityName: string;
  projectName: string;
  readOnly?: boolean;
  disableRunLinks?: boolean;
  singleRun?: boolean; // set to true in RunWorkspace, false otherwise (determines if we use LinePlot or RunsLinePlot)
  panelSettingsRef: PanelSettingsTypes.Ref;
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  customRunColorsRef: CustomRunColorsViewTypes.Ref;
  runSetRefs: RunSetViewTypes.Ref[];
  showSlowWarning?: boolean;
  showCliVersionWarning?: boolean;
  hideSlowWarning?(): void;
  hideCliVersionWarning?(): void;
}

function historyWeight(historyKeyInfo: RunHistoryKeyInfo): number {
  let totalSteps = 0;
  for (const set of historyKeyInfo.sets) {
    let hasSystem = false;
    for (const key of set.keys) {
      if (key.startsWith('system/')) {
        hasSystem = true;
        break;
      } else if (!isReservedKey(key)) {
        break;
      }
    }
    if (!hasSystem) {
      totalSteps += set.count;
    }
  }
  let estimatedSize = 0;
  for (const key of Object.keys(historyKeyInfo.keys)) {
    if (key.startsWith('system/')) {
      continue;
    }
    const info = historyKeyInfo.keys[key];
    for (const tc of info.typeCounts) {
      let valSize = key.length;
      switch (tc.type) {
        case 'number':
          valSize += 8;
          break;
        case 'string':
          valSize += 16;
          break;
        case 'histogram':
          valSize += 1024;
          break;
        default:
          // this will include things like images which have some metadata about them
          valSize += 256;
          break;
      }
      estimatedSize += valSize * tc.count;
    }
  }

  let estimatedRuns = Math.round(
    totalSteps / Math.max(1, historyKeyInfo.lastStep || 0)
  );
  estimatedRuns = Math.min(100, Math.max(1, estimatedRuns));

  return estimatedSize / estimatedRuns;
}

const PanelBank: React.FC<PanelBankProps> = makeComp(
  props => {
    const {
      entityName,
      projectName,
      readOnly,
      panelBankConfigRef,
      panelSettingsRef,
      runSetRefs,
      customRunColorsRef,
      hideSlowWarning,
      hideCliVersionWarning,
      singleRun,
    } = props;
    const [
      searchQuery,
      debouncedSearchQuery,
      setSearchQuery,
    ] = useDebounceState('', 750);
    const [panelBankWidth, setPanelBankWidth] = useState(0);
    const keyInfoQuery = useKeyInfoQuery(props.runSetRefs);
    const setPanelSelection = InteractStateContext.useInteractStateAction(
      InteractStateActions.setPanelSelection
    );

    const slowHistory = useMemo(() => {
      if (keyInfoQuery.loading || keyInfoQuery.error != null) {
        return false;
      }
      /* tslint:disable:no-bitwise */
      return historyWeight(keyInfoQuery.historyKeyInfo) > 500 << 20; // 500 MB
      /* tslint:enable:no-bitwise */
    }, [keyInfoQuery]);

    useEffect(() => {
      if (slowHistory && props.showSlowWarning) {
        analytics.track('Workspace Slow Warning');
      }
    }, [slowHistory, props.showSlowWarning]);

    if (keyInfoQuery.loading || keyInfoQuery.error != null) {
      return <Loader active size="huge" />;
    }
    const {historyKeyInfo, viz: historyKeyViz} = keyInfoQuery;

    return (
      <>
        <div className="panel-bank">
          {slowHistory && props.showSlowWarning && (
            <WarningMessage
              content={
                <p>
                  You're logging a lot of data – this page may load slowly.{' '}
                  <a href={docUrl.limits}>Learn more →</a>
                </p>
              }
              className="slow-warning"
              onDismiss={hideSlowWarning}
            />
          )}
          {props.showCliVersionWarning && (
            <WarningMessage
              content={
                <p>
                  Your version of wandb is outdated. Please upgrade with: pip
                  install wandb --upgrade
                </p>
              }
              className="slow-warning"
              onDismiss={hideCliVersionWarning}
            />
          )}
          <PanelBankControls
            entityName={entityName}
            projectName={projectName}
            readOnly={readOnly}
            panelBankConfigRef={panelBankConfigRef}
            panelSettingsRef={panelSettingsRef}
            runSetRefs={runSetRefs}
            customRunColorsRef={customRunColorsRef}
            searchQuery={searchQuery}
            debouncedSearchQuery={debouncedSearchQuery}
            setSearchQuery={setSearchQuery}
            singleRun={singleRun}
            historyKeyInfo={historyKeyInfo}
            panelBankWidth={panelBankWidth}
          />
          <PanelBankInner
            panelBankProps={props}
            warningVisible={slowHistory && props.showSlowWarning}
            debouncedSearchQuery={debouncedSearchQuery}
            historyKeyInfo={historyKeyInfo}
            historyKeyViz={historyKeyViz}
            panelBankWidth={panelBankWidth}
            setPanelBankWidth={setPanelBankWidth}
            setPanelSelection={setPanelSelection}
          />
        </div>
      </>
    );
  },
  {id: 'PanelBank', memo: true}
);

interface PanelBankInnerProps {
  panelBankProps: PanelBankProps;
  warningVisible?: boolean;
  debouncedSearchQuery: string;
  historyKeyInfo: RunHistoryKeyInfo;
  historyKeyViz: {
    [key: string]: any;
  };
  panelBankWidth: number;
  setPanelBankWidth(width: number): void;
  setPanelSelection(args: any): any;
}

const PanelBankInner = makeComp(
  (props: PanelBankInnerProps) => {
    const {
      panelBankProps,
      warningVisible,
      debouncedSearchQuery,
      historyKeyInfo,
      historyKeyViz,
      panelBankWidth,
      setPanelBankWidth,
      setPanelSelection,
    } = props;

    return (
      <Measure
        bounds
        onResize={contentRect => {
          setPanelBankWidth(
            contentRect.bounds
              ? contentRect.bounds.width - PANEL_BANK_PADDING * 2
              : 0
          );
        }}>
        {({measureRef}) => {
          return (
            <div
              ref={measureRef}
              onMouseDown={e => {
                if (
                  (e.target as Element).closest('.panel-bank__panel') == null
                ) {
                  setPanelSelection([]);
                }
              }}>
              <div className="panel-bank__sections">
                {panelBankWidth > 0 && (
                  <PanelBankSections
                    key={panelBankProps.panelBankConfigRef.id}
                    {...panelBankProps}
                    warningVisible={warningVisible}
                    historyKeyInfo={historyKeyInfo}
                    historyKeyViz={historyKeyViz}
                    searchQuery={debouncedSearchQuery}
                    panelBankWidth={panelBankWidth}
                  />
                )}
              </div>
            </div>
          );
        }}
      </Measure>
    );
  },
  {
    id: 'PanelBankInner',
    memo: (prevProps, nextProps) => {
      return (
        prevProps.debouncedSearchQuery === nextProps.debouncedSearchQuery &&
        prevProps.historyKeyInfo === nextProps.historyKeyInfo &&
        prevProps.panelBankWidth === nextProps.panelBankWidth &&
        prevProps.setPanelBankWidth === nextProps.setPanelBankWidth &&
        prevProps.setPanelSelection === nextProps.setPanelSelection &&
        shallowEqual(prevProps.panelBankProps, nextProps.panelBankProps)
      );
    },
  }
);

const PanelBankWithContext: React.FC<PanelBankProps> = makeComp(
  props => (
    <PanelBankContextProvider>
      <PanelBank {...props} />
    </PanelBankContextProvider>
  ),
  {id: 'PanelBankWithContext', memo: true}
);

export default PanelBankWithContext;
